import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AgentInstructionsList from '../Components/AgentInstructionsList';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import PageLoader from '../Components/PageLoader';
import TestAgentModal from '../Components/TestAgentModal';

import { ConfirmationProvider } from '../Components/ConfirmationContext';
import { AgentApiProvider, useAgentApi } from '../Services/AgentApiProvider';
import {
  AssistantInstructionApi,
  AssistantInstructionListApi,
} from '../Services/AgentApiProviderTypes';
import { PromptApi } from '../Services/PromptApiProviderTypes';

function AgentInstructionsContent({ canEdit }: { canEdit: boolean }) {
  const navigate = useNavigate();
  const { organizationId, agentId } = useParams();

  const isDisabled = !canEdit;

  const { getAgentById, getAgentInstructions, getRelevantInstruction, getPrompts } = useAgentApi();

  const [loading, setLoading] = useState(false);

  // ── Agent info (for header & test panel) ──────────────────────────────
  const [name, setName] = useState<string>('');
  const [promptId, setPromptId] = useState<string>('');
  const [prompts, setPrompts] = useState<PromptApi[]>([]);
  const [isPromptRealtime, setIsPromptRealtime] = useState<boolean>(false);

  // ── Instructions state ────────────────────────────────────────────────
  const [agentInstructions, setAgentInstructions] = useState<AssistantInstructionApi[]>([]);
  const [totalInstructions, setTotalInstructions] = useState<number>(0);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [instructionsLoading, setInstructionsLoading] = useState<boolean>(false);
  const [currentSearchTerm, setCurrentSearchTerm] = useState<string>('');
  const [onUpdate, setOnUpdate] = useState<boolean>(false);

  const updateTrigger = (): void => setOnUpdate((prev) => !prev);

  // ── Prompt realtime detection ─────────────────────────────────────────
  useEffect(() => {
    if (!promptId || !prompts?.length) {
      setIsPromptRealtime(false);
      return;
    }
    const selectedPrompt = prompts.find((p) => p.id === promptId);
    setIsPromptRealtime(selectedPrompt?.pattern_type === 'realtime');
  }, [promptId, prompts]);

  // ── Fetch agent info ──────────────────────────────────────────────────
  useEffect(() => {
    const f = async () => {
      if (agentId === 'new-agent') return;

      setLoading(true);
      try {
        const [agent, promptsList] = await Promise.all([getAgentById(agentId), getPrompts()]);

        setName(agent.name);
        setPromptId(agent.prompt_id);
        setPrompts(promptsList);
      } catch (err) {
        console.error('Error fetching agent data:', err);
      } finally {
        setLoading(false);
      }
    };

    f();
  }, [agentId]);

  // ── Fetch instructions ────────────────────────────────────────────────
  const fetchInstructions = useCallback(
    async (skip: number, limit: number, searchTerm = '') => {
      if (!agentId || agentId === 'new-agent') return;

      setInstructionsLoading(true);
      try {
        let responseData: AssistantInstructionListApi;
        if (searchTerm) {
          responseData = await getRelevantInstruction(agentId, searchTerm, limit);
        } else {
          responseData = await getAgentInstructions(agentId, skip, limit);
        }
        setAgentInstructions(responseData.items || []);
        setTotalInstructions(responseData.totalCount || 0);
      } catch (err) {
        if (err.response && err.response.status === 404) {
          setAgentInstructions([]);
          setTotalInstructions(0);
        }
      } finally {
        setInstructionsLoading(false);
      }
    },
    [agentId, getAgentInstructions, getRelevantInstruction],
  );

  const handleSearch = useCallback(
    (searchTerm: string): void => {
      setCurrentSearchTerm(searchTerm);
      fetchInstructions(0, itemsPerPage, searchTerm);
    },
    [fetchInstructions, itemsPerPage],
  );

  const handleTableChange = (skip: number, limit: number): void => {
    if (limit && limit !== itemsPerPage) {
      setItemsPerPage(limit);
    }
    fetchInstructions(skip, limit);
  };

  useEffect(() => {
    fetchInstructions(0, itemsPerPage, currentSearchTerm);
  }, [onUpdate, itemsPerPage, currentSearchTerm]);

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-6">
          <div className="d-flex justify-between align-center">
            <h2 className="h2-title m-0">{name}</h2>

            <div className="buttons-container d-flex justify-end">
              <Button
                variant="secondary"
                size="large"
                onClick={() => navigate(`/organizations/${organizationId}/agents-hub/${agentId}`)}
              >
                View Settings
              </Button>

              <Button
                variant="secondary"
                size="large"
                onClick={() =>
                  navigate(
                    `/organizations/${organizationId}/agents-hub/${agentId}/audit-operations`,
                  )
                }
              >
                Audit Operations
              </Button>
            </div>
          </div>

          <div className="gap d-flex flex-row-to-col flex-column">
            <div className="int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr">
              <AgentInstructionsList
                agentId={agentId}
                canEdit={!isDisabled}
                agentInstructions={agentInstructions}
                onUpdateTrigger={updateTrigger}
                onSearch={handleSearch}
                loading={instructionsLoading}
                totalInstructions={totalInstructions}
                itemsPerPage={itemsPerPage}
                onChangePage={handleTableChange}
              />
            </div>
          </div>
        </div>

        <div className="radius-yes p-2 bg-yes radius col-6">
          <h2 className="h2-title">Test Agent</h2>
          <div className="gap d-flex border-yes radius-yes flex-row-to-col flex-column border-yes">
            <TestAgentModal isOpen={true} isPromptRealtime={isPromptRealtime} />
          </div>
        </div>
      </div>
    </div>
  );
}

function AgentInstructions({ canEdit }: { canEdit: boolean }) {
  return (
    <ConfirmationProvider>
      <AgentApiProvider>
        <AgentInstructionsContent canEdit={canEdit} />
      </AgentApiProvider>
    </ConfirmationProvider>
  );
}

export default AgentInstructions;
